/**
 * In-memory fallback for `localStorage` / `sessionStorage` when the browser
 * has web storage switched off (#871).
 *
 * Android WebViews / in-app browsers with DOM storage disabled expose
 * `window.localStorage` as `null` rather than an empty store, and some
 * privacy modes throw a `SecurityError` on access. The app reads storage
 * unguarded in ~15 places (auth tokens, device id, notification read date,
 * banner dismissals), several of them in mount effects, so the first one to
 * run throws `Cannot read properties of null (reading 'getItem')` and the
 * whole tree falls to the error boundary.
 *
 * Swapping in a memory-backed `Storage` keeps the site usable. The cost is
 * that nothing persists across a reload — including the sign-in — which is
 * strictly better than the error screen.
 *
 * Must run before the first React render — see the call in `src/index.js`.
 */

type StorageName = "localStorage" | "sessionStorage";

export const createMemoryStorage = (): Storage => {
  const data = new Map<string, string>();
  const storage = {
    get length() {
      return data.size;
    },
    key: (index: number) => Array.from(data.keys())[index] ?? null,
    getItem: (key: string) =>
      data.has(key) ? (data.get(key) as string) : null,
    setItem: (key: string, value: string) => {
      data.set(String(key), String(value));
    },
    removeItem: (key: string) => {
      data.delete(key);
    },
    clear: () => data.clear(),
  };
  // `Object.keys(localStorage)` (LoggerContext) must list the stored keys, as
  // it does on a real Storage — so expose them as own enumerable properties.
  return new Proxy(storage, {
    ownKeys: () => Array.from(data.keys()),
    getOwnPropertyDescriptor: (target, prop) =>
      typeof prop === "string" && data.has(prop)
        ? { value: data.get(prop), enumerable: true, configurable: true }
        : Reflect.getOwnPropertyDescriptor(target, prop),
  }) as Storage;
};

const isUsable = (name: StorageName): boolean => {
  try {
    return window[name] != null;
  } catch {
    return false; // SecurityError on access.
  }
};

export const installStorageShim = (): void => {
  if (typeof window === "undefined") return;

  (["localStorage", "sessionStorage"] as StorageName[]).forEach((name) => {
    if (isUsable(name)) return;
    try {
      Object.defineProperty(window, name, {
        value: createMemoryStorage(),
        configurable: true,
      });
    } catch {
      // A non-configurable accessor can't be replaced; leaving it alone is no
      // worse than before the shim.
    }
  });
};
